/**
 * EVOLUTION audio engine — AudioApi implementation on rendered audio assets.
 *
 * - graph.ts      context, buses (music/sfx/voice), ducking, reverb, limiter, 3D listener
 * - samples.ts    recorded SFX bank (public/audio/sfx, built by scripts/sfx)
 * - stream.ts     rendered orchestral music: looping calm + hot stems (scripts/music)
 * - narration.ts  the Scientist's TTS voice lines (scripts/tts-narrator.mjs)
 * - ambient.ts    continuous noise beds (wind, rain, water…)
 * - lab.ts        `?audiolab=1` QA page
 *
 * Stage agents may still `register(name, factory)` procedural sounds (helpers in
 * `@audio/sfx` + `@audio/kit`); a recorded sample with the same name takes precedence.
 */
import type { Vector3 } from 'three';
import type { AudioApi, NarratorMood, SfxFactory, SfxOptions } from '@core/services';
import { clamp } from './dsp';
import { AudioGraph, createContext, type Volumes } from './graph';
import { Ambience } from './ambient';
import { setSharedReverb } from './kit';
import { Narration, type Line } from './narration';
import { SampleBank } from './samples';
import type { Kit } from './sfx';
import { SFX_META } from './sfxmeta';
import { MusicPlayer } from './stream';

/** Global cap on simultaneous SFX instances (lower on mobile). */
const MAX_VOICES_DESKTOP = 28;
const MAX_VOICES_MOBILE = 16;
/** Spatial sounds beyond this distance (world units) are culled unless high priority. */
const CULL_DISTANCE = 140;

interface Active {
  name: string;
  end: number;
  priority: number;
}

export class AudioEngine implements AudioApi {
  private graph?: AudioGraph;
  private musicEngine?: MusicPlayer;
  private ambience?: Ambience;
  private samples?: SampleBank;
  private narration?: Narration;
  private custom = new Map<string, SfxFactory>();
  private volumes: Volumes = { master: 0.8, music: 0.7, sfx: 0.9, voice: 0.9 };
  private wantTrack: string | null = null;
  private lastStageTrack: string | null = null;
  private wantFade = 1.5;
  private intensity = 0;
  private active: Active[] = [];
  private lastPlayed = new Map<string, number>();
  private recent = new Map<string, number[]>();
  private warned = new Set<string>();
  private line?: Line;
  private listenerPos?: { x: number; y: number; z: number };
  private gestureInstalled = false;
  unlocked = false;

  constructor() {
    if (typeof window === 'undefined') return;
    this.installGestureUnlock();
    document.addEventListener('visibilitychange', () => this.onVisibility());
    window.addEventListener('pageshow', () => this.onVisibility());
    // QA handle (level checks in headless Chrome, audio lab).
    (window as unknown as { __EVO_AUDIO__: AudioEngine }).__EVO_AUDIO__ = this;
    if (new URLSearchParams(location.search).has('audiolab')) {
      void import('./lab').then((m) => m.mountAudioLab(this));
    }
  }

  // ---------------------------------------------------------------- lifecycle

  private ensure(): AudioGraph | undefined {
    if (this.graph) return this.graph;
    const ctx = createContext();
    if (!ctx) return undefined;
    this.graph = new AudioGraph(ctx);
    this.graph.setVolumes(this.volumes);
    setSharedReverb(ctx, this.graph.sfxReverbSend);
    this.musicEngine = new MusicPlayer(this.graph);
    this.ambience = new Ambience(this.graph);
    this.samples = new SampleBank(this.graph);
    this.narration = new Narration(this.graph);
    this.musicEngine.setIntensity(this.intensity);
    ctx.addEventListener('statechange', () => {
      const running = ctx.state === 'running';
      if (running && !this.unlocked) this.onUnlocked();
      this.unlocked = running;
    });
    return this.graph;
  }

  /** Resume on the first gesture anywhere (capture phase, before UI handlers), and after iOS interruptions. */
  private installGestureUnlock(): void {
    if (this.gestureInstalled) return;
    this.gestureInstalled = true;
    const events = ['pointerdown', 'touchstart', 'touchend', 'mousedown', 'keydown', 'click'];
    const handler = () => {
      if (this.graph?.ctx.state === 'running') return;
      void this.unlock();
    };
    for (const e of events) window.addEventListener(e, handler, { capture: true, passive: true });
  }

  async unlock(): Promise<void> {
    const g = this.ensure();
    if (!g) return;
    const ctx = g.ctx;
    // iOS needs a buffer started synchronously inside the gesture.
    try {
      const b = ctx.createBuffer(1, 1, 22050);
      const s = ctx.createBufferSource();
      s.buffer = b;
      s.connect(ctx.destination);
      s.start(0);
    } catch {
      /* ignore */
    }
    if (ctx.state !== 'running') {
      try {
        await Promise.race([ctx.resume(), new Promise((r) => setTimeout(r, 800))]);
      } catch {
        /* ignore */
      }
    }
    const running = (ctx.state as AudioContextState) === 'running';
    if (running && !this.unlocked) this.onUnlocked();
    this.unlocked = running;
  }

  private onUnlocked(): void {
    this.unlocked = true;
    // Decode the SFX bank + warm the voice manifest and the first music cues.
    void this.samples!.load();
    this.narration!.warm();
    const m = this.musicEngine!;
    if (this.wantTrack !== m.currentName) m.play(this.wantTrack, this.wantFade);
    setTimeout(() => {
      void m.prefetch('menu');
      void m.prefetch('intro');
    }, 300);
  }

  private onVisibility(): void {
    const g = this.graph;
    if (!g) return;
    const hidden = document.visibilityState === 'hidden';
    g.setHidden(hidden);
    if (hidden) {
      this.line?.stop();
      this.clearPendingBurps();
      // Let the fade finish, then suspend to save battery.
      setTimeout(() => {
        if (document.visibilityState === 'hidden' && g.ctx.state === 'running') void g.ctx.suspend().catch(() => undefined);
      }, 600);
    } else if (this.unlocked || g.ctx.state !== 'closed') {
      void g.ctx.resume().catch(() => undefined);
    }
  }

  // ---------------------------------------------------------------- mixing

  setVolumes(v: { master: number; music: number; sfx: number; voice: number }): void {
    this.volumes = { ...v };
    this.graph?.setVolumes(this.volumes);
  }

  duck(amount: number, seconds: number): void {
    this.graph?.duck(clamp(amount, 0, 1), seconds);
  }

  setListener(position: Vector3, forward: Vector3): void {
    this.listenerPos = position;
    const g = this.graph;
    if (!g || g.ctx.state !== 'running') return;
    g.setListener(position, forward);
  }

  // ---------------------------------------------------------------- music

  music(track: string | null, opts?: { fade?: number }): void {
    // Leaving a stage (transition / menu / ending / intro / silence): fade stage ambients.
    // Also when moving to a different stage (a 'boss' detour and back keeps them).
    const meta = track === null || track === 'transition' || track === 'menu' || track === 'ending' || track === 'intro';
    if (meta || (track !== 'boss' && track !== this.lastStageTrack)) this.ambience?.clear();
    if (track && !meta && track !== 'boss') this.lastStageTrack = track;
    this.wantTrack = track;
    this.wantFade = opts?.fade ?? 1.5;
    const m = this.musicEngine;
    if (!m || this.graph!.ctx.state !== 'running') return;
    if (m.currentName === track) return;
    m.play(track, this.wantFade);
  }

  ambient(name: string, level: number, opts?: { pitch?: number }): void {
    const g = this.graph;
    if (!g || g.ctx.state !== 'running') return;
    this.ambience!.set(name, level, opts?.pitch ?? 1);
  }

  setIntensity(v: number): void {
    this.intensity = clamp(v, 0, 1);
    this.musicEngine?.setIntensity(this.intensity);
  }

  // ---------------------------------------------------------------- sfx

  register(name: string, factory: SfxFactory): void {
    this.custom.set(name, factory);
  }

  sfx(name: string, opts: SfxOptions = {}): void {
    const g = this.graph;
    if (!g || g.ctx.state !== 'running') return;
    if (name === 'burp') {
      // One burp per *burp*: a pending voice burp is replaced by this one; a just-played one wins.
      if (g.now - this.lastBurp < 1.5) return;
      if (this.pendingBurps.length) clearTimeout(this.pendingBurps.shift());
      this.lastBurp = g.now;
    }
    this.play(name, opts);
  }

  private play(name: string, opts: SfxOptions = {}): void {
    const g = this.graph!;
    const ctx = g.ctx;
    const now = ctx.currentTime;
    const meta = SFX_META[name] ?? {};
    const custom = this.custom.get(name);
    const bank = this.samples!;
    const sampled = bank.has(name);
    if (!sampled && !custom) {
      // Still decoding (first seconds after unlock) or genuinely missing: stay silent.
      if (bank.known(name) || this.warned.has(name)) return;
      if (this.samples && this.samplesSettled) {
        this.warned.add(name);
        if (import.meta.env.DEV) console.warn(`[audio] no sample for "${name}"`);
      }
      return;
    }
    const priority = meta.priority ?? 1;

    // Retrigger cooldown.
    const cd = meta.cooldown ?? 0.015;
    const last = this.lastPlayed.get(name) ?? -1;
    if (now - last < cd) return;

    // Distance cull for spatial sounds.
    let dist = 0;
    if (opts.position && this.listenerPos) {
      dist = g.distanceToListener(opts.position);
      if (dist > CULL_DISTANCE && priority < 3) return;
    }

    // Voice limiting: per-name polyphony, then a global budget that drops low priority first.
    this.active = this.active.filter((a) => a.end > now);
    const poly = meta.poly ?? 3;
    if (this.active.filter((a) => a.name === name).length >= poly) return;
    const max = g.isMobile ? MAX_VOICES_MOBILE : MAX_VOICES_DESKTOP;
    if (this.active.length >= max) {
      if (priority < 2) return;
      let idx = -1;
      for (let i = 0; i < this.active.length; i++) {
        if (this.active[i].priority < priority && (idx < 0 || this.active[i].priority < this.active[idx].priority)) idx = i;
      }
      if (idx < 0) return;
      this.active.splice(idx, 1);
    }
    this.lastPlayed.set(name, now);

    const variance = opts.variance ?? 0.04;
    const p = (opts.pitch ?? 1) * (1 + (Math.random() * 2 - 1) * variance);
    // Repetition fatigue: rapid repeats of the same sound get progressively quieter.
    const recent = (this.recent.get(name) ?? []).filter((t) => now - t < 1);
    recent.push(now);
    this.recent.set(name, recent);
    const fatigue = Math.max(0.55, 1 / (1 + 0.12 * Math.max(0, recent.length - 2)));
    const vol = Math.max(0, opts.volume ?? 1) * fatigue;

    // Per-sound gain → optional panner → sfx bus.
    const gain = ctx.createGain();
    gain.gain.value = vol;
    const dest = opts.position ? g.spatial(opts.position, g.sfx) : g.sfx;
    gain.connect(dest);
    const rev = ctx.createGain();
    rev.gain.value = opts.position ? clamp(12 / Math.max(12, dist), 0.15, 1) : 1;
    rev.connect(g.sfxReverbSend);

    let end: number;
    const played = sampled ? bank.play(name, gain, rev, p, now) : false;
    if (played !== false) end = played;
    else {
      try {
        custom!(ctx, gain, { ...opts, pitch: p });
      } catch (e) {
        if (import.meta.env.DEV) console.warn(`[audio] custom sfx "${name}" threw`, e);
      }
      end = now + 1.5;
    }
    if (meta.duck) g.duck(meta.duck[0], meta.duck[1]);
    this.active.push({ name, end, priority });
    // Disconnect the per-sound nodes after the tail so the graph stays small.
    const ms = Math.max(200, (end - now + 3) * 1000);
    setTimeout(() => {
      gain.disconnect();
      rev.disconnect();
      if (dest !== g.sfx) dest.disconnect();
    }, ms);
  }

  private get samplesSettled(): boolean {
    return (this.graph?.now ?? 0) > 8;
  }

  /** Build a Kit for sfx helpers (tone/noise/fm) routed into `out`. */
  makeKit(out: AudioNode, rev?: AudioNode, pitch = 1, vol = 1): Kit {
    const g = this.graph!;
    return { ctx: g.ctx, out, rev: rev ?? g.sfxReverbSend, t: g.ctx.currentTime + 0.005, p: pitch, v: vol, noise: (c) => g.noiseBuffer(c) };
  }

  // ---------------------------------------------------------------- voice

  voice(text: string, opts?: { mood?: NarratorMood }): void {
    const g = this.graph;
    if (!g || g.ctx.state !== 'running' || !text) return;
    this.line?.stop();
    this.clearPendingBurps();
    const mood = opts?.mood ?? 'snark';
    void this.narration!.speak(text, mood).then((line) => {
      if (!line) return;
      this.line = line;
      g.duck(0.5, line.duration + 0.25);
      // Burps come from here unless the UI plays its own sfx('burp') first (de-duplicated).
      for (const at of line.burps) {
        const timer = window.setTimeout(() => {
          this.pendingBurps = this.pendingBurps.filter((p) => p !== timer);
          if (g.ctx.state !== 'running' || g.now - this.lastBurp < 1.5) return;
          this.lastBurp = g.now;
          this.play('burp', { volume: 0.8 });
        }, at * 1000);
        this.pendingBurps.push(timer);
      }
    });
  }

  private pendingBurps: number[] = [];
  private lastBurp = -10;

  private clearPendingBurps(): void {
    for (const t of this.pendingBurps) clearTimeout(t);
    this.pendingBurps = [];
  }

  // ---------------------------------------------------------------- QA / lab helpers

  /** Internal access for the audio lab and QA hooks. */
  get debug(): { graph?: AudioGraph; music?: MusicPlayer; samples?: SampleBank; intensity: number; voices: number; track: string | null } {
    return {
      graph: this.graph,
      music: this.musicEngine,
      samples: this.samples,
      intensity: this.intensity,
      voices: this.active.filter((a) => a.end > (this.graph?.now ?? 0)).length,
      track: this.musicEngine?.currentName ?? null,
    };
  }
}
